<template>
  <div class="container-fluid mt-4">
    <b-breadcrumb :items="breadcrumb" />
    <h1
      class="h1"
      style="text-align:center;"
    >
      Provision a New Project
    </h1>
    <b-row>
      <b-col
        lg="5"
        class="col-centered"
      >
        <b-card title="Project">
          <form
            @submit.prevent="submitRequest"
            @reset="onReset"
          >
            <b-form-group
              label="Project Name"
              :description="descriptions.projectName"
              :invalid-feedback="invalidFeedbacks.projectName"
              :state="validateProjectName && validateDuplicateProjectName"
            >
              <b-form-input
                v-model="model.projectName"
                type="text"
                :state="validateProjectName && validateDuplicateProjectName"
              />
            </b-form-group>

            <b-form-group
              label="Department"
              :description="descriptions.departmentId"
              :invalid-feedback="invalidFeedbacks.departmentId"
              :state="validateDepartment"
            >
              <b-form-input
                v-model="model.departmentId"
                type="text"
                :state="validateDepartment"
              />
            </b-form-group>

            <b-form-group
              label="Product"
              :description="descriptions.financeProduct"
              :invalid-feedback="invalidFeedbacks.financeProduct"
              :state="validateFinanceProduct"
            >
              <b-input-group>
                <b-form-select
                  v-model="financeProductName"
                  :options="financeProductNames"
                />
                <b-input-group-append>
                  <b-input-group-text
                    v-b-hover="handleFinanceProductInfoHover"
                    :class="financeProductInfoHover ? 'text-muted pointer-cursor' : ''"
                    @click="showFinanceProductInfoModal"
                  >
                    <font-awesome-icon
                      id="finance-product-info-icon"
                      icon="info-circle"
                    />
                  </b-input-group-text>
                  <b-modal
                    ref="financeProductInfoModal"
                    title="What's 'Product' For?"
                    hide-footer
                    centered
                  >
                    Used by finance to assign costs to business groups.
                  </b-modal>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>

            <b-form-group
              label="Product Description"
              :description="descriptions.productDescription"
              :invalid-feedback="invalidFeedbacks.productDescription"
              :state="validateProductDescription"
            >
              <b-form-input
                v-model="model.productDescription"
                type="text"
                :state="validateProductDescription"
              />
            </b-form-group>

            <b-form-group
              label="Security Level"
              :description="descriptions.securityLevel"
              :invalid-feedback="invalidFeedbacks.securityLevel"
            >
              <b-input-group>
                <b-form-select
                  v-model="model.securityLevel"
                  :options="securityLevelList"
                />
                <b-input-group-append>
                  <b-input-group-text
                    v-b-hover="handleSecurityLevelInfoHover"
                    @click="showSecurityLevelInfoModal"
                  >
                    <font-awesome-icon
                      id="security-level-info-icon"
                      icon="info-circle"
                      :class="securityLevelInfoHover ? 'text-muted pointer-cursor' : ''"
                    />
                  </b-input-group-text>
                  <b-modal
                    ref="securityLevelInfoModal"
                    hide-footer
                    title="How to choose?"
                    centered
                  >
                    <ul>
                      <li>
                        <strong>security-critical:</strong> Private access only to the team. Code contains trade secrets.
                        Exposure of this code to competitors would cause significant financial harm to MSI. <br>
                        Patented algorithms or functionality.
                      </li>
                      <li>
                        <strong>security-medium (default):</strong> Code cannot be open sourced. Code is customer facing functionality
                        that contains features provided to our customers.
                      </li>
                      <li>
                        <strong>security-low:</strong> If the other two levels do not apply, select this level.
                      </li>
                    </ul>
                  </b-modal>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>

            <b-form-group
              :description="descriptions.usApprover"
              :state="validateUsApproverCoreId"
              :invalid-feedback="invalidFeedbacks.usApprover"
            >
              <UsersTagInput
                v-model="model.usApprover"
                label="US Approver"
                :minimum-users="0"
              />
            </b-form-group>

            <b-form-group
              :description="descriptions.cccsApprover"
              :state="validateCccsApproverCoreId"
              :invalid-feedback="invalidFeedbacks.cccsApprover"
            >
              <UsersTagInput
                v-model="model.cccsApprover"
                label="CCCS Approver"
                :minimum-users="0"
              />
            </b-form-group>

            <b-form-group
              :description="descriptions.nonSovereignApprover"
              :state="validateNonSovereignApproverCoreId"
              :invalid-feedback="invalidFeedbacks.nonSovereignApprover"
            >
              <UsersTagInput
                v-model="model.nonSovereignApprover"
                label="Non Sovereign Approver"
                :minimum-users="0"
              />
            </b-form-group>

            <b-form-group
              :description="descriptions.securityChampion"
              :state="validateSecurityChampionCoreId"
              :invalid-feedback="invalidFeedbacks.securityChampion"
            >
              <UsersTagInput
                v-model="model.securityChampion"
                label="Security/Compliance Champions"
                :minimum-users="0"
                :maximum-users="4"
              />
            </b-form-group>

            <b-form-group
              :description="descriptions.pdm"
              :state="validatePdmCoreId"
              :invalid-feedback="invalidFeedbacks.pdm"
            >
              <UsersTagInput
                v-model="model.pdm"
                label="PDMs"
                :minimum-users="1"
                :maximum-users="4"
              />
            </b-form-group>

            <b-form-group
              :description="descriptions.devManager"
              :state="validateDevManagerCoreId"
              :invalid-feedback="invalidFeedbacks.devManager"
            >
              <UsersTagInput
                v-model="model.devManager"
                label="Dev Managers"
                :minimum-users="1"
                :maximum-users="4"
              />
            </b-form-group>

            <UsersTagInput
              v-model="model.owners"
              :minimum-users="2"
              label="Project Owners"
            />
            <UsersTagInput
              v-model="model.users"
              :minimum-users="1"
              label="Project Contributors"
            />
            <UsersTagInput
              v-model="model.logReaders"
              :minimum-users="1"
              label="Log Readers"
            />
            <UsersTagInput
              v-model="model.metricReaders"
              :minimum-users="1"
              label="Metric Readers"
            />
            <br>
            <b-form-group label="Azure Environments">
              <treeselect
                v-model="model.environments"
                :multiple="true"
                :auto-load-root-options="false"
                :options="options"
                :load-options="loadOptions"
                :value-consists-of="valueConsistsOf"
                :required="false"
              />
            </b-form-group>

            <b-form-group
              v-if="canAccessAws"
              label="AWS Environments"
            >
              <treeselect
                v-model="model.awsEnvironments"
                :multiple="true"
                :auto-load-root-options="false"
                :options="awsOptions"
                :value-consists-of="valueConsistsOf"
                :required="false"
              />
            </b-form-group>
            <div>
              <b-btn
                :disabled="disableButton"
                type="submit"
                variant="success"
              >
                Submit Request
              </b-btn>
              <b-button
                type="reset"
                variant="danger"
              >
                Reset
              </b-button>
            </div>
          </form>
        </b-card>
      </b-col>
    </b-row>
    <div
      v-if="loading === true"
      id="loading-div"
    >
      <img
        id="loading-icon"
        src="../../static/img/ping-loader.gif"
        alt="Responsive image"
      >
      <p id="loading-test">
        Requesting...
      </p>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import Vue from 'vue';
import VueToastr2 from 'vue-toastr-2';
import 'vue-toastr-2/dist/vue-toastr-2.min.css';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import _ from 'lodash';
import options from '@/logger-options';
import VueLogger from 'vuejs-logger';
import toastr from 'toastr';

import UsersTagInput from '@/components/UsersTagInput.vue';
import {StakeHoldersMaxLimit} from '../constants/StakeHoldersMaxLimit';
window.toastr = toastr;

Vue.use(VueToastr2);
Vue.use(VueLogger, options);


const defaultModel = {
  departmentId: '',
  devManager: [],
  environments: [],
  financeProduct: '',
  logReaders: [],
  metricReaders: [],
  owners: [],
  pdm: [],
  productDescription: '',
  projectName: '',
  securityChampion: [],
  securityLevel: 'security-medium',
  usApprover: [],
  cccsApprover: [],
  nonSovereignApprover: [],
  users: [],
  awsEnvironments: [],
};

export default {
  components: {
    Treeselect,
    UsersTagInput,
  },
  data() {
    return {
      name: '',
      isCIEAdmin: false,
      userInputValidation: {},
      model: {...defaultModel},
      environments: [],
      popUp: '',
      popUpStatus: '',
      breadcrumb: [
        {
          text: 'Home',
          to: '/',
        },
        {
          text: 'New Project',
          active: true,
        },
      ],
      descriptions: {
        departmentId: 'Department ID that owner affiliates with. You can find it in MSI Directory Services.',
        devManager: `Please enter your dev manager's CoreID.(Max ${StakeHoldersMaxLimit.devManager} only)`,
        financeProduct: 'Select a product from the dropdown.',
        pdm: `Please enter your product development manager's CoreID. (Max ${StakeHoldersMaxLimit.pdm} only)`,
        projectName: 'Used as the prefix for Azure Resource Group and VSTS project names. Please separate words with a dash (-).',
        productDescription: 'Briefly describe your product.',
        securityChampion: `Please enter your Security/Compliance champion's CoreID.(Max ${StakeHoldersMaxLimit.securityChampion} only)`,
        securityLevel: 'Select a security level.',
        usApprover: `Please enter your US approver's CoreID.(Max ${StakeHoldersMaxLimit.UsApprover} only)`,
        cccsApprover: `Please enter your CCCS approver's CoreID.(Max ${StakeHoldersMaxLimit.CccsApprover} only)`,
        nonSovereignApprover: `Please enter your Non Sovereign approver's CoreID.(Max ${StakeHoldersMaxLimit.NonSovereignApprover} only)`,
      },
      financeProductInfoHover: false,
      financeProducts: [],
      disableButton: true,
      invalidFeedbacks: {
        departmentId: 'Required: the department ID.',
        devManager: `Maximum ${StakeHoldersMaxLimit.devManager} CoreIDs are allowed`,
        financeProduct: 'Product is required',
        pdm: `Maximum ${StakeHoldersMaxLimit.pdm} CoreIDs are allowed`,
        projectName: 'Sorry, space and special characters ("/[]:|<>+=;?*,.) are not allowed Or the project name is already taken.',
        productDescription: 'Sorry, only letters, numbers and space are allowed.',
        securityChampion: `Maximum ${StakeHoldersMaxLimit.securityChampion} CoreIDs are allowed`,
        securityLevel: 'Security level is required.',
        usApprover: `Maximum ${StakeHoldersMaxLimit.UsApprover} CoreIDs are allowed`,
        cccsApprover: `Maximum ${StakeHoldersMaxLimit.CccsApprover} CoreIDs are allowed`,
        nonSovereignApprover: `Maximum ${StakeHoldersMaxLimit.NonSovereignApprover} CoreIDs are allowed`,
      },
      validFeedbacks: 'Thank you',
      loading: false,
      valueConsistsOf: 'LEAF_PRIORITY',
      options: null,
      awsOptions: [
        {id: 'dev',
          label: 'DEV US Commercial',
          children: [{
            id: JSON.stringify({
              name: 'dev/us-east-1',
              status: 'Requested',
            }),
            label: 'dev-us-east-1',
          }],
        },
        {
          id: 'prod',
          isDisabled: true,
          label: 'PROD US Commercial',
          children: [{
            id: JSON.stringify({
              name: 'prod/us-east-1',
              status: 'Requested',
            }),
            label: 'prod-us-east-1',
          }],
        },
        {
          id: 'gov-prod',
          isDisabled: true,
          label: 'PROD US GOV',
          children: [{
            id: JSON.stringify({
              name: 'gov-prod/us-gov-east-1',
              status: 'Requested',
            }),
            label: 'us-gov-east-1',
          }],
        },
      ],
      securityLevelInfoHover: false,
      securityLevelList: [
        {value: 'security-critical', text: 'security-critical'},
        {value: 'security-medium', text: 'security-medium'},
        {value: 'security-low', text: 'security-low'},
      ],
      selectedEnvs: [],
      validPdmCoreId: false,
      validDevManagerCoreId: false,
      validUsApproverCoreId: true,
      validCccsApproverCoreId: true,
      validNonSovereignApproverCoreId: true,
      validSecurityChampionCoreId: true,
      MaxPdmCoreId: StakeHoldersMaxLimit.pdm,
      MaxDevManagerCoreId: StakeHoldersMaxLimit.devManager,
      MaxUsApproverCoreId: StakeHoldersMaxLimit.UsApprover,
      MaxCccsApproverCoreId: StakeHoldersMaxLimit.CccsApprover,
      MaxNonSovereignApproverCoreId: StakeHoldersMaxLimit.NonSovereignApprover,
      MaxSecurityChampionCoreId: StakeHoldersMaxLimit.securityChampion,
      projectRes: [],
      projectNamesList: [],
    };
  },
  computed: {
    financeProductName: {
      get() {
        if (this.model.financeProduct) {
          return this.model.financeProduct.name;
        }
        return null;
      },
      set(value) {
        this.model.financeProduct = this.financeProducts.find((fp) => fp.name === value);
      },
    },
    financeProductNames() {
      return this.financeProducts.map((fp) => fp.name);
    },
    validateDepartment() {
      if (this.model.departmentId.length === 0) {
        return null;
      } else {
        return true;
      }
    },
    validateFinanceProduct() {
      if (!this.model.financeProduct) {
        return false;
      }
      return this.financeProductNames.includes(this.model.financeProduct.name);
    },
    validateProductDescription() {
      if (this.model.productDescription.length === 0) {
        return null;
      } else if (this.model.productDescription.match(/^[a-zA-Z0-9_ ]*$/g) === null) {
        return false;
      } else {
        return true;
      }
    },
    validateProjectName() {
      const pn = this.model.projectName;
      const projectNameRegex = /^[a-zA-Z0-9]{1}((?![~@%#$'"{}/\\[\]:|<>+=;?*,\s]).)*$/g;
      if (pn.length === 0) {
        return null;
      } else if (pn.endsWith('.') || pn.match(projectNameRegex) === null) {
        return false;
      } else {
        return true;
      }
    },
    canAccessAws() {
      return this.isCIEAdmin? true:false;
    },
  },
  asyncComputed: {
    async validateUsApproverCoreId() {
      if (this.model.usApprover.length === 0) {
        this.validUsApproverCoreId = true; // optional
      } else if (this.model.usApprover.length >=1 && this.model.usApprover.length <= this.MaxUsApproverCoreId) {
        this.validUsApproverCoreId = await this.validateCoreIds(this.model.usApprover);
        if (this.validUsApproverCoreId) {
          this.disableButton = this.checkIfAllFieldsAreTrue();
        }
      } else {
        this.validUsApproverCoreId = false;
        this.disableButton = true;
      }
      return this.validUsApproverCoreId;
    },
    async validateCccsApproverCoreId() {
      if (this.model.cccsApprover.length === 0) {
        this.validCccsApproverCoreId = true; // optional
      } else if (this.model.cccsApprover.length >=1 && this.model.cccsApprover.length <= this.MaxCccsApproverCoreId) {
        this.validCccsApproverCoreId = await this.validateCoreIds(this.model.cccsApprover);
        if (this.validCccsApproverCoreId) {
          this.disableButton = this.checkIfAllFieldsAreTrue();
        }
      } else {
        this.validCccsApproverCoreId = false;
        this.disableButton = true;
      }
      return this.validCccsApproverCoreId;
    },
    async validateNonSovereignApproverCoreId() {
      if (this.model.nonSovereignApprover.length === 0) {
        this.validNonSovereignApproverCoreId = true; // optional
      } else if (this.model.nonSovereignApprover.length >=1 &&
       this.model.nonSovereignApprover.length <= this.MaxNonSovereignApproverCoreId) {
        this.validNonSovereignApproverCoreId = await this.validateCoreIds(this.model.nonSovereignApprover);
        if (this.validNonSovereignApproverCoreId) {
          this.disableButton = this.checkIfAllFieldsAreTrue();
        }
      } else {
        this.validNonSovereignApproverCoreId = false;
        this.disableButton = true;
      }
      return this.validNonSovereignApproverCoreId;
    },
    async validateSecurityChampionCoreId() {
      if (this.model.securityChampion.length === 0) {
        this.validSecurityChampionCoreId = true;
      } else if (this.model.securityChampion.length >=1 && this.model.securityChampion.length <= this.MaxSecurityChampionCoreId) {
        this.validSecurityChampionCoreId = await this.validateCoreIds(this.model.securityChampion);
        if (this.validSecurityChampionCoreId) {
          this.disableButton = this.checkIfAllFieldsAreTrue();
        }
      } else {
        this.validSecurityChampionCoreId = false;
        this.disableButton = true;
      }
      return this.validSecurityChampionCoreId;
    },
    async validatePdmCoreId() {
      if (this.model.pdm.length === 0) {
        this.validPdmCoreId = false; // required
      } else if (this.model.pdm.length >=1 && this.model.pdm.length <= this.MaxPdmCoreId) {
        this.validPdmCoreId = await this.validateCoreIds(this.model.pdm);
        if (this.validPdmCoreId) {
          this.disableButton = this.checkIfAllFieldsAreTrue();
        }
      } else {
        this.validPdmCoreId = false;
        this.disableButton = true;
      }
      return this.validPdmCoreId;
    },
    async validateDevManagerCoreId() {
      if (this.model.devManager.length === 0) {
        this.validDevManagerCoreId = false; // required
      } else if (this.model.devManager.length >= 1 && this.model.devManager.length <= this.MaxDevManagerCoreId) {
        this.validDevManagerCoreId = await this.validateCoreIds(this.model.devManager);
        if (this.validDevManagerCoreId) {
          this.disableButton = this.checkIfAllFieldsAreTrue();
        }
      } else {
        this.validDevManagerCoreId = false;
        this.disableButton = true;
      }
      return this.validDevManagerCoreId;
    },
    async validateDuplicateProjectName() {
      const pn = this.model.projectName.toLowerCase();
      const pnPresnet = this.projectNamesList.includes(pn);
      if (pnPresnet) {
        this.disableButton = this.checkIfAllFieldsAreTrue();
        return false;
      } else {
        this.disableButton = this.checkIfAllFieldsAreTrue();
        return true;
      }
    },
  },
  async created() {
    this.financeProducts = await api.getFinanceProducts();
  },
  async mounted() {
    try {
      const response = await this.getEnvironmentsFromApi();
      if (!response) {
        this.$router.push({name: 'error', params: {errorMessage: 'Unable to load data. Connect to MSI VPN and try again!'}});
      }

      // filter all environments down to 'available' environments
      const allEnvs = response;
      const availableEnvs = [];
      for (const environ of allEnvs) {
        if (environ.status !== 'closed' && environ.status !== 'admin-only') {
          const availableSubEnvs = [];
          for (const subEnviron of environ['subEnvironments']) {
            if (subEnviron.status !== 'closed' && subEnviron.status !== 'admin-only') {
              availableSubEnvs.push(subEnviron);
            }
          }
          if (availableSubEnvs.length > 0) {
            environ['subEnvironments'] = availableSubEnvs;
            availableEnvs.push(environ);
          }
        }
      }
      this.environments = availableEnvs;
      this.getItems();
    } catch (error) {
      console.error(error);
    }
  },
  methods: {
    async checkInput() {
      const prepareMessage = (message) => {
        return `<strong>${message}</strong><br />`;
      };
      const baseMessage = 'Invalid or Missing required info:<br/>';
      this.popUp = baseMessage;
      if (!this.model.projectName || this.model.projectName.length === 0) {
        this.popUp += prepareMessage('Project name');
      }
      if (!this.validateDuplicateProjectName) {
        this.popUp += prepareMessage('Project name already taken');
      }
      if (!this.model.departmentId || this.model.departmentId.length === 0) {
        this.popUp += prepareMessage('Department ID');
      }
      if (!this.model.financeProduct) {
        this.popUp += prepareMessage('Product');
      }
      if (!this.model.productDescription || this.model.productDescription.length === 0) {
        this.popUp += prepareMessage('Product description');
      }
      if (!this.model.securityLevel || this.model.securityLevel.length === 0) {
        this.popUp += prepareMessage('Security Level');
      }
      const checkCoreIds = async (type, coreIds, minimumNumber = 0) => {
        if (!coreIds || !(coreIds.length >= minimumNumber)) {
          return prepareMessage(`${type} CoreIDs (min. ${minimumNumber})`);
        } else {
          const valid = await this.validateCoreIds(coreIds);
          if (!valid) {
            return prepareMessage(`${type} CoreIDs (invalid ids)`);
          }
        }
        return '';
      };
      const checkOptionalCoreId = async (type, coreId) => {
        if (coreId.length !== 0) {
          const valid = await this.validateCoreIds(coreId);
          if (!valid) {
            return prepareMessage(`${type} (invalid id)`);
          }
        }
        return '';
      };
      const checkRequiredCoreId = async (type, coreId) => {
        if (!coreId || coreId.length === 0) {
          return prepareMessage(`${type} (missing id)`);
        } else {
          const valid = await this.validateCoreIds(coreId);
          if (!valid) {
            return prepareMessage(`${type} (invalid id)`);
          }
        }
        return '';
      };
      const promises = [
        checkOptionalCoreId('US Approver', this.model.usApprover),
        checkOptionalCoreId('CCCS Approver', this.model.cccsApprover),
        checkOptionalCoreId('Non Sovereign Approver', this.model.nonSovereignApprover),
        checkOptionalCoreId('Security Champion', this.model.securityChampion),
        checkRequiredCoreId('PDM', this.model.pdm),
        checkRequiredCoreId('Dev Manager', this.model.devManager),
        checkCoreIds('Owners', this.model.owners, 1),
        checkCoreIds('Contributors', this.model.users, 1),
        checkCoreIds('Log readers', this.model.logReaders, 1),
        checkCoreIds('Metric readers', this.model.metricReaders, 1),
      ];
      const results = await Promise.all(promises);
      for (const result of results) {
        this.popUp += result;
      }
      return this.popUp === baseMessage;
    },
    failFeedback() {
      this.$toastr.error(this.popUp, '', {
        positionClass: 'toast-bottom-center',
        timeOut: '200000',
        toastClass: 'toast-style',
      });
    },
    /**
     * get only environments (and sub environments) that are not closed or restricted
     *
     * @return {array} environments
     */
    getEnvironmentsFromApi() {
      return api.getEnvironments();
    },
    /**
     * The loadOptions method is used by the TreeSelect component in order to convert from API returned values to
     * options required by the component.
     * @param {string} action - Action performed on the component
     * @param {function} callback - a function that accepts an optional error argument
     * @return {Promise<void>}
     */
    async loadOptions({action, callback}) {
      try {
        const sorted = _.sortBy(this.environments, [
          function (e) {
            return e.sortOrder;
          },
        ]);
        const grouped = _.groupBy(sorted, 'regionName');
        this.options = [];
        for (const currentEnvironment in grouped) {
          if ({}.hasOwnProperty.call(grouped, currentEnvironment)) {
            const parent = {
              id: currentEnvironment,
              label: currentEnvironment,
            };
            const children = [];
            for (const currentSubscription of grouped[currentEnvironment]) {
              children.push({
                id: JSON.stringify({
                  env: _.lowerCase(currentSubscription['environment']),
                  cloud: currentSubscription['azureRegion'],
                }),
                label: `${_.upperCase(currentSubscription['environment'])} ${currentSubscription['regionName']}`,
                isDisabled: currentSubscription['environment'].toLowerCase().startsWith('prod'),
              });
            }
            parent['children'] = children;

            if (currentEnvironment.toLowerCase().startsWith('prod')) {
              parent['isDisabled'] = true;
            }
            this.options.push(parent);
          }
        }
      } catch (e) {
        this.$log.error(e);
      }
    },

    // async loadAWSOptions({action, callback}) {
    //   await this.loadOptions({action, callback});
    //   const awsEnv = this.options.filter((env)=>{
    //     if (env.id == 'US Commercial') {
    //       return true;
    //     }
    //   });
    //   awsEnv.forEach((ele) => {
    //     ele.children=ele.children.filter((child)=>{
    //       if (child.label == 'DEV US Commercial') {
    //         return true;
    //       }
    //     });
    //   });
    //   this.awsOptions = awsEnv;
    // },
    handleFinanceProductInfoHover(hovered) {
      this.financeProductInfoHover = hovered;
    },
    handleSecurityLevelInfoHover(hovered) {
      this.securityLevelInfoHover = hovered;
    },
    onReset(evt) {
      evt.preventDefault();
      /* Reset our form values */
      this.model = {...defaultModel};
    },
    showFinanceProductInfoModal() {
      this.$refs['financeProductInfoModal'].show();
    },
    showSecurityLevelInfoModal() {
      this.$refs['securityLevelInfoModal'].show();
    },
    async submitRequest() {
      this.model.environments = this.model.environments.map((env) => JSON.parse(env));
      // this.model.awsEnvironments = this.model.awsEnvironments.map((env) => JSON.parse(env));
      if (!(await this.checkInput())) {
        this.failFeedback();
        return;
      }
      const data = {};
      this.loading = true;
      // eslint-disable-next-line max-len
      const userTypes = ['owners', 'users', 'logReaders', 'metricReaders', 'pdm', 'devManager', 'usApprover', 'cccsApprover', 'nonSovereignApprover', 'securityChampion'];
      for (const i in this.model) {
        if (userTypes.includes(i)) {
          data[i] = this.model[i].map((p) => p.trim().toUpperCase());
        } else if (i === 'productDescription') {
          // "productDescription" is "product" in the back-end.
          data.product = this.model[i];
        } else {
          data[i] = this.model[i];
        }
      }
      data.financeProduct = {id: this.model.financeProduct.id};
      data.pdm = Array.from(new Set(data.pdm)).sort();
      data.devManager = Array.from(new Set(data.devManager)).sort();
      data.usApprover = Array.from(new Set(data.usApprover)).sort();
      data.cccsApprover = Array.from(new Set(data.cccsApprover)).sort();
      data.nonSovereignApprover = Array.from(new Set(data.nonSovereignApprover)).sort();
      data.securityChampion = Array.from(new Set(data.securityChampion)).sort();
      data.users = Array.from(new Set(data.users)).sort();
      data.owners = Array.from(new Set(data.owners)).sort();
      data.logReaders = Array.from(new Set(data.logReaders)).sort();
      data.metricReaders = Array.from(new Set(data.metricReaders)).sort();
      data.securityLevel = this.model.securityLevel;
      try {
        await api.createProject(data);
        this.successFeedback();
      } catch (err) {
        if (err.response && err.response.data && err.response.data.error) {
          this.popUp = 'Error: ' + err.response.data.error;
        } else {
          this.popUp = 'Some error occured while creating project';
        }
        this.failFeedback();
      }
      this.loading = false;
      this.model = {...defaultModel}; // reset form
    },
    successFeedback() {
      this.$toastr.success('Success! Check your project! <br> <a href="/projects">Click here to Status page</a>', '', {
        positionClass: 'toast-bottom-center',
        timeOut: '200000',
        toastClass: 'toast-style',
      });
    },
    async validateCoreIds(coreIds) {
      const results = await api.validateCoreIds(coreIds);
      return (results.length === 0);
    },
    checkIfAllFieldsAreTrue () {
      if (this.validDevManagerCoreId &&this.validPdmCoreId && this.validSecurityChampionCoreId && this.validUsApproverCoreId &&
       this.validateDuplicateProjectName) {
        return false;
      } else {
        return true;
      }
    },
    async getItems() {
      const projectsRes = await api.getProjects();
      this.isCIEAdmin = projectsRes.mode === 'admin';
      // console.log('isAdmin>>', this.isCIEAdmin);
      const projects = projectsRes.projects.map((item) => item.projectName.toLowerCase());
      this.projectNamesList = Array.from(new Set(projects));
    },
  },
};
</script>

<style>
.col-centered {
  float: none;
  margin: 0 auto;
}
#loading-div {
  position: fixed;
  bottom: 0;
  left: 85%;
}
#loading-test {
  color: rgb(238, 132, 83);
  padding-left: 35%;
}
#loading-icon {
  width: 100%;
  height: 100%;
}
</style>
